import { createPopper, Instance, Modifier, ModifierArguments, Options } from '@popperjs/core';
import { autoinject, bindingMode, Container, customAttribute, TaskQueue, View, ViewEngine } from 'aurelia-framework';
import { DOM } from 'aurelia-pal';
import { bindable } from 'aurelia-typed-observable-plugin';
import { debounce } from 'framework/script_utilities/calls-limiter';
import { log } from 'framework/script_utilities/dev-decorators';
import { resolve } from 'framework/script_utilities/object-navigator';
import maxSize from 'popper-max-size-modifier';
import { watch } from "aurelia-watch-decorator";

@autoinject
@customAttribute('af-autocomplete')
export class AutocompleteCustomAttribute {
  @bindable({ defaultBindingMode: bindingMode.twoWay }) item: unknown = null;
  @bindable({ primaryProperty: true }) options: SpecOrItems = null;
  @bindable valuePath = '';
  @bindable stickMode = false;
  @bindable valueMatcher: ValueMatcher = 'includes';
  @bindable templatePath = null;

  private input: HTMLInputElement;
  private list: HTMLUListElement;
  private popper: Instance;
  private view: View;
  private config: AutocompleteConfig;
  private updateDebounced: () => void;
  private needsUpdate = true;
  private fromList = false;
  private toggleEnabled = false;
  private lastQueryId = 0;
  private shownQueryId = 0;

  items: unknown[] = [];
  selectedIndex = -1;
  active = false;
  visible = false;
  boundText = '';
  isLoading = false;

  constructor(element: Element, private container: Container, private viewEngine: ViewEngine, private taskQueue: TaskQueue) {
    this.input = element as HTMLInputElement;
  }

  bind() {
    if (this.input.value === '')
      this.updateValue(false);
    this.updateConfig(false);
    if (this.config.showOnFocus)
      this.updateDebounced();
  }

  attached() {
    this.input.addEventListener('input', this.inputListener);
    this.input.addEventListener('focus', this.inputFocusListener);
    this.input.addEventListener('mousedown', this.inputMousedownListener);
    this.input.addEventListener('blur', this.inputBlurListener);
    this.input.addEventListener('keydown', this.inputKeydownListener);
    this.create();
  }

  detached() {
    this.input.removeEventListener('input', this.inputListener);
    this.input.removeEventListener('focus', this.inputFocusListener);
    this.input.removeEventListener('mousedown', this.inputMousedownListener);
    this.input.removeEventListener('blur', this.inputBlurListener);
    this.input.removeEventListener('keydown', this.inputKeydownListener);
    this.destroy();
  }

  private create() {
    this.viewEngine.loadViewFactory(this.templatePath ? this.templatePath : DEFAULT_TEMPLATE).then(factory => {
      const childContainer = this.container.createChild();
      this.view = factory.create(childContainer);

      this.list = document.createElement('ul');
      this.list.classList.add(CLASS_COMPONENT);
      this.list.addEventListener('mousedown', this.listMousedownListener);

      this.view.bind(this);
      this.view.appendNodesTo(this.list);

      this.input.parentNode.insertBefore(this.list, this.input.nextSibling);
      this.popper = createPopper(this.input, this.list, {
        placement: 'auto',
        modifiers: [maxSize, sameWidthModifier, maxHeightModifier, { name: 'flip', options: { allowedAutoPlacements: ['bottom', 'top'] } }]
      });
    });
  }
  private destroy() {
    if (this.popper) (this.popper.destroy(), this.popper = null);
    if (this.list) (this.list.remove(), this.list = null);
    if (this.view) (this.view.unbind(), this.view = null);
  }

  private syncVisibility() {
    const show = () => {
      this.visible = true;
      this.selectedIndex = 0;
      this.list.addEventListener('mousedown', this.listMousedownListener);
      document.addEventListener('mousedown', this.documentMousedownListener);
      document.addEventListener('mouseup', this.documentMouseupListener);
      this.list.classList.add(CLASS_VISIBLE);
      this.popper.forceUpdate();
      this.popper.forceUpdate();
    }

    const hide = () => {
      this.visible = false;
      this.selectedIndex = -1;
      this.list.classList.remove(CLASS_VISIBLE);
      this.list.removeEventListener('mousedown', this.listMousedownListener);
      document.removeEventListener('mousedown', this.documentMousedownListener);
      document.removeEventListener('mouseup', this.documentMouseupListener);
    }

    this.active && this.items.length > 0
      ? !this.visible && show()
      : this.visible && hide();

    this.dispatchEmptyResultEvent();
  }

  private activate() {
    if (this.active)
      return false;
    this.active = true;
    this.needsUpdate
      ? this.updateDebounced()
      : this.syncVisibility();
    return true;
  }

  private deactivate() {
    if (!this.active)
      return false;
    this.active = false;
    this.syncVisibility();
    return true;
  }

  get currentText() {
    return this.input.value.trim() ?? '';
  }

  get selectedItem() {
    return !!~this.selectedIndex ? this.items[this.selectedIndex] : null;
  }

  selectItem(item: unknown) {
    this.item !== item ? this.item = item : this.updateValue(false);
    this.input.focus();
    this.deactivate();
  }

  scrollSelectionIntoView() {
    this.taskQueue.queueMicroTask(() => this.list.querySelector('li.--selected')?.scrollIntoView({ block: 'nearest' }));
  }

  private selectIndex(condition: boolean, index: number) {
    if (!condition)
      return false;
    this.selectedIndex = index;
    this.scrollSelectionIntoView();
    return true;
  }

  private selectNext = () => this.selectIndex(this.selectedIndex < this.items.length - 1, this.selectedIndex + 1);
  private selectPrev = () => this.selectIndex(this.selectedIndex > 0, this.selectedIndex - 1);
  private selectLast = () => this.selectIndex(this.selectedIndex !== this.items.length - 1, this.items.length - 1);
  private selectFirst = () => this.selectIndex(this.selectedIndex !== 0, 0);

  getValue(item: unknown) {
    return item == null ? '' : String(this.valuePath ? resolve(item, this.valuePath) : item);
  }

  @log('debug')
  private async updateItems() {
    this.isLoading = true;

    const update = (items: unknown[], text: string) => {
      const item = this.selectedItem;
      const currentIndex = this.selectedIndex;
      const newIndex = items.findIndex(i => this.getValue(i) === this.getValue(item));
      this.boundText = text;
      this.items = items;
      this.selectedIndex =
        this.items.length === 0 ? -1 :
          item == null || currentIndex === 0 || newIndex === -1 ? 0 :
            newIndex;
    }

    this.needsUpdate = false;
    const text = this.currentText;

    const queryId = ++this.lastQueryId;
    const queryResult = text.length >= this.config.minLength ? await this.config.getItems(text) : [];
    if (queryId > this.shownQueryId) {
      update(queryResult, text);
      this.shownQueryId = queryId;
      this.syncVisibility();
    }
    this.isLoading = false;
  }

  private updateValue(allowUpdate: boolean) {
    const value = this.getValue(this.item);
    if (this.input.value !== value) {
      this.input.value = value;
      this.input.dispatchEvent(DOM.createCustomEvent('change'));
      this.active && allowUpdate
        ? this.updateDebounced()
        : this.needsUpdate = true;
    }
  }

  private updateConfig(allowUpdate: boolean) {
    this.config = Array.isArray(this.options)
      ? new ArraySource(this.options, this)
      : { ...new AutocompleteConfig(), ...this.options };
    this.updateDebounced = debounce(this.updateItems, this.config.debounceMs);
    this.active && allowUpdate
      ? this.updateDebounced()
      : this.needsUpdate = true;
  }

  itemChanged() {
    this.updateValue(true);
  }

  optionsChanged() {
    this.updateConfig(true);
  }

  private inputListener = () => {
    if (this.input.value.length === 0)
      this.item = null;
    this.needsUpdate = true;
    if (!this.activate())
      this.updateDebounced();
  }

  private listMousedownListener = () => this.fromList = true;
  private documentMousedownListener = (e: MouseEvent) => !this.fromList && e.target !== this.input && this.deactivate();
  private documentMouseupListener = () => this.fromList = false;
  private inputFocusListener = () => (this.config.showOnFocus && this.activate(), setTimeout(() => this.toggleEnabled = true, 250));
  private inputMousedownListener = () => this.toggleEnabled && (this.active ? this.deactivate() : this.activate());
  private inputBlurListener = () => (this.stickMode && this.updateValue(false), !this.fromList && this.deactivate(), this.toggleEnabled = false);

  private inputKeydownListener = (e: KeyboardEvent) => {
    switch (e.code) {
      case Key.Enter: return this.onEnter(e);
      case Key.Esc: return this.onEscape(e);
      case Key.ArrowDown: return this.onArrowDown(e);
      case Key.ArrowUp: return this.onArrowUp(e);
      case Key.End: return this.onEnd(e);
      case Key.Home: return this.onHome(e);
    }
  }

  private onEnter(e: KeyboardEvent) {
    if (this.visible) {
      this.selectItem(this.selectedItem);
      e.preventDefault();
    }
  }

  private onEscape(e: KeyboardEvent) {
    if (this.stickMode)
      this.updateValue(false);
    if (this.visible) {
      this.deactivate();
      e.preventDefault();
    }
  }

  private onArrowDown(e: KeyboardEvent) {
    if (this.input.selectionStart === this.input.value.length)
      if (this.visible ? this.selectNext() : this.activate())
        e.preventDefault();
  }

  private onArrowUp(e: KeyboardEvent) {
    if (this.visible && this.selectPrev())
      e.preventDefault();
  }

  private onEnd(e: KeyboardEvent) {
    if (this.input.selectionStart === this.input.value.length)
      if (this.visible && this.selectLast())
        e.preventDefault();
  }

  private onHome(e: KeyboardEvent) {
    if (this.visible && this.selectFirst())
      e.preventDefault();
  }

  dispatchEmptyResultEvent() {
    let showNotFound = false;

    if (this.input.value.length > 0 && this.items.length == 0 && this.active && this.stickMode) {
      showNotFound = true;
    }
    this.input.dispatchEvent(
      new CustomEvent("af-autocomplete:dispatchEmptyResultsEvent", {
        detail: showNotFound,
        bubbles: true,
      })
    );
  }

  @watch("isLoading")
  loadingChanged() {
    this.input.dispatchEvent(
      new CustomEvent("af-autocomplete:loadingChanged", {
        detail: this.isLoading,
        bubbles: true,
      })
    );
  }
}

class ArraySource implements AutocompleteConfig {
  minLength = 0;
  debounceMs = 0;
  showOnFocus = true;

  constructor(private items: unknown[], private source: AutocompleteCustomAttribute) { }

  private match(item: unknown, text: string) {
    const value = this.source.getValue(item).toLowerCase();
    return this.source.valueMatcher === 'startsWith' ? value.startsWith(text) : !!~value.indexOf(text);
  }

  async getItems(text: string): Promise<unknown[]> {
    text = text.toLowerCase();
    return this.items.filter(item => this.match(item, text));
  }
}

const DEFAULT_TEMPLATE = 'framework/html_behaviors/af-autocomplete.html';
const CLASS_COMPONENT = 'af-autocomplete';
const CLASS_VISIBLE = '--open';

const sameWidthModifier: Modifier<string, ModifierArguments<Options>> = {
  name: 'sameWidth', enabled: true, phase: 'beforeWrite', requires: ['computeStyles'],
  fn: ({ state }) => { state.styles.popper.width = `${state.rects.reference.width}px`; },
  effect: ({ state }) => { state.elements.popper.style.width = `${(state.elements.reference as HTMLElement).offsetWidth}px`; }
}

const maxHeightModifier: Modifier<string, ModifierArguments<Options>> = {
  name: 'maxHeight', enabled: true, phase: 'beforeWrite', requires: ['maxSize'],
  fn: ({ state }) => { state.styles.popper.maxHeight = `${Math.max(state.modifiersData.maxSize.height - 10, 0)}px`; state.styles.popper.overflowY = 'auto'; }
}

class AutocompleteConfig {
  getItems: QueryFunction = async () => [];
  minLength = 2;
  debounceMs = 100;
  showOnFocus = false;
}

type SpecOrItems = AutocompleteOptions | unknown[];
type ValueMatcher = 'startsWith' | 'includes';
type QueryFunction = (text: string) => Promise<unknown[]>;
export type AutocompleteOptions = Partial<AutocompleteConfig>;

enum Key {
  Enter = 'Enter',
  Esc = 'Escape',
  ArrowUp = 'ArrowUp',
  ArrowDown = 'ArrowDown',
  Home = 'Home',
  End = 'End'
}
